<template>
  <div class="tw-w-full tw-text-sm">
    <slot name="header" :set-invalid="setInvalid" />

    <div v-bind="$attrs">
      <loading-table v-if="error" has-error />
      <loading-table v-else-if="loading" />

      <div
        v-else-if="itemList.length"
        ref="tableDiv"
        v-dragscroll.x="!touchScreen"
        class="tw-max-w-[100vw]"
        :class="noScroll ? 'tw-overflow-visible' : 'tw-overflow-x-auto'"
        @scroll="checkScroll"
      >
        <table class="tw-w-full tw-border-separate" style="border-spacing: 0px">
          <thead v-if="!rowSchema.noHeader">
            <tr class="tw-font-bold">
              <table-header
                v-for="(
                  { actionCell, cellClass, sortable, label, skipTranslation },
                  index
                ) in headers"
                :key="index"
                :sort="sort"
                :sort-options="getSortOption(label)"
                :has-right-scroll="actionCell && hasRightScroll"
                :sortable="sortable"
                :label="label || ''"
                :skip-translation="skipTranslation"
                :class="cellClass"
                @update:sort="(sort) => updateTable('sort', sort)"
              />
            </tr>
          </thead>
          <tbody>
            <table-row
              v-for="(item, index) in itemList"
              :key="item[rowId]"
              :item="item"
              :row-schema="rowSchema.config"
              :row-index="index"
              :has-right-scroll="hasRightScroll"
              :clickable="clickable"
              @click="$emit('click:row', item)"
            />
          </tbody>
        </table>
      </div>
      <no-table-data v-else :text="noDataText" />
    </div>

    <pagination-container
      v-show="itemList.length && !loading && (itemTotal > limit || simple)"
      :id="tableId"
      table-pagination
      @update="(action) => updateTable('pagination', action)"
    />
  </div>
</template>

<script>
import { computed, nextTick, provide, inject, toRefs, watch, ref } from 'vue'
import PaginationContainer from '@components/List/PaginationContainer.vue'
import LoadingTable from '@components/Loading/LoadingTable.vue'
import NoTableData from '@components/Table/NoTableData.vue'
import TableHeader from '@components/Table/TableHeader.vue'
import { isFunction, clone } from '@helpers/utils.js'
import TableRow from '@components/Table/TableRow.vue'
import { dragscroll } from 'vue-dragscroll'
import { useBrowse } from '@composables'
import { BREAKPOINTS } from '@config'
import {
  useEventListener,
  onClickOutside,
  useBreakpoints,
  onKeyUp,
} from '@vueuse/core'

export default {
  components: {
    PaginationContainer,
    LoadingTable,
    NoTableData,
    TableHeader,
    TableRow,
  },
  directives: {
    dragscroll,
  },
  inheritAttrs: false,
  props: {
    tableId: {
      type: String,
      required: true,
    },
    rowSchema: {
      type: Object,
      required: true,
    },
    // Required so devs explicitly confirm
    // they need to handle fetch events
    fetch: {
      type: Boolean,
      required: true,
    },
    simple: {
      type: Boolean,
      default: false,
    },
    nextPageUrl: {
      type: String,
      default: null,
    },
    rowId: {
      type: String,
      default: 'id',
    },
    items: {
      type: Object,
      default: () => {},
    },
    loading: {
      type: Boolean,
    },
    error: {
      type: String,
      default: null,
    },
    noDataText: {
      type: String,
      default: null,
    },
    limit: {
      type: Number,
      default: 10,
    },
    itemTotal: {
      type: Number,
      default: 0,
    },
    clickable: {
      type: Boolean,
      default: false,
    },
    query: {
      type: Object,
      default: () => ({}),
    },
    noScroll: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'fetch:paginate-next',
    'fetch:paginate-prev',
    'fetch:query',
    'click:row',
  ],
  setup(props) {
    const {
      nextPageUrl,
      itemTotal,
      rowSchema,
      tableId,
      simple,
      items,
      fetch,
      query,
      limit,
    } = toRefs(props)

    const { smaller } = useBreakpoints(BREAKPOINTS)
    const mdAndDown = smaller('lg')
    const hasLeftScroll = ref(false)
    const hasRightScroll = ref(false)
    const tableDiv = ref(null)

    const touchScreen = 'ontouchstart' in document.documentElement

    provide('mdAndDown', mdAndDown)

    const headers = computed(() =>
      rowSchema.value.config.map((cell) => (isFunction(cell) ? cell({}) : cell))
    )

    const sortOptions = computed(() =>
      clone(headers.value).map((r) => ({
        label: r.label,
        key: r.key,
        default: r.key && r.key === rowSchema.value.defaultSort,
        ...r.sort,
      }))
    )

    const { range, sort, itemList, updateTable, setInvalid } = useBrowse({
      id: tableId.value,
      items,
      total: itemTotal,
      sortOptions: sortOptions.value,
      limit,
      query: query.value,
      fetch,
      simple: simple.value,
      nextPageUrl,
    })

    if (fetch.value) {
      setInvalid()
    }

    const updateActiveId = inject('updateActiveId', () => {})
    onClickOutside(tableDiv, () => {
      updateActiveId(null)
    })

    onKeyUp('Escape', () => {
      updateActiveId(null)
    })

    function getSortOption(label) {
      return sortOptions.value.find((option) => option.label === label)
    }

    function checkScroll() {
      const scrollLeftMax =
        tableDiv.value?.scrollWidth - tableDiv.value?.clientWidth - 4

      hasRightScroll.value =
        tableDiv.value?.scrollWidth > tableDiv.value?.clientWidth &&
        tableDiv.value?.scrollLeft < scrollLeftMax

      hasLeftScroll.value = tableDiv.value?.scrollLeft > 0
    }

    useEventListener('resize', checkScroll)

    watch(
      tableDiv,
      () => {
        // timeout required otherwise scrollWidth will be incorrect on page load
        nextTick(() => setTimeout(checkScroll, 500))
      },
      { immediate: true }
    )

    return {
      hasRightScroll,
      getSortOption,
      updateTable,
      touchScreen,
      checkScroll,
      setInvalid,
      itemList,
      tableDiv,
      headers,
      // required for unit test
      range,
      sort,
    }
  },
}
</script>

<style>
table {
  thead {
    tr {
      border-top-left-radius: var(--tile-border-radius);
    }

    th {
      &:first-child {
        border-top-left-radius: var(--tile-border-radius);
      }
      &:last-child {
        border-top-right-radius: var(--tile-border-radius);
      }
    }
  }
  tr:last-child {
    td:first-child {
      border-bottom-left-radius: var(--tile-border-radius);
      overflow: hidden;
    }
    td:last-child {
      border-bottom-right-radius: var(--tile-border-radius);
    }
  }
}
</style>
